import React, { useCallback, useMemo } from 'react';
import CustomSwitch from '@/components/CostomSwitch/CustomSwitch';
import appointmentStyles from '@/ctComponents/AppointmentTable/AppointmentTable.module.css';
import AutobookTimeDateSelector from '@/screens/Autobook/AutobookTimeDateSelector';
import { AutobookRuleWarehouseGroupsSection } from './AutobookRuleWarehouseGroupsSection';

const FIELD_W = '23.8%';

const inputStyle = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
};

const DROP_PICK_TITLE = 'Drop & Pick Matching Logic';
const DROP_PICK_SUBTITLE =
  'Smart Turn first tries same warehouse matching, then optional same-city balancing. Warehouse groups can be used both as rule scope and as approved sharing groups for balancing logic.';
const DROP_PICK_SUBTITLE_2 =
  'Select hours, days, dates, or terminals where AutoBook should NOT schedule appointments.';
const SHARING_GROUPS_INTRO =
  'Select warehouse groups that are approved for same-city balancing and shared empty-container logic. This is separate from rule scope groups on the first tab.';

export const AutobookRuleStepDropPick = ({
  swUseOldestEmptyFirst,
  setSwUseOldestEmptyFirst,
  swSameWarehouseReuse,
  setSwSameWarehouseReuse,
  swSameCityReuse,
  setSwSameCityReuse,
  swMaxDistance,
  setSwMaxDistance,
  swMinExcessThreshold,
  setSwMinExcessThreshold,
  swBeforeTargerReturn,
  setSwBeforeTargerReturn,
  swAfterTargerReturn,
  setSwAfterTargerReturn,
  catalogWarehouseGroups,
  selectedSwSharingGroupCatalogIds,
  setSelectedSwSharingGroupCatalogIds,
  catalogCustomers,
  catalogWarehouses,
  draftRuleUuid,
  swRulePreferences,
  onSwRulePreferencesChange,
  preferencesSyncKey,
}) => {
  const autobookModeOff = useMemo(() => ({ status: false }), []);
  const noopHistory = useCallback(() => {}, []);

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col gap-5">
      <section className="box-border flex w-full min-w-0 max-w-full flex-col rounded-lg border border-solid border-[#e8e8e8] bg-white p-4">
        <h2 className="m-0 shrink-0 text-left text-base font-bold leading-snug text-[#131517]">
          {DROP_PICK_TITLE}
        </h2>
        <p className="m-0 mt-2 max-w-[920px] shrink-0 text-left text-sm leading-relaxed text-[#717784]">
          {DROP_PICK_SUBTITLE}
        </p>

        <div className="mt-5 flex w-full min-w-0 flex-row flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex shrink-0 items-center gap-2">
            <CustomSwitch
              checked={swUseOldestEmptyFirst}
              onChange={(next) => setSwUseOldestEmptyFirst(next)}
            />
            <span className="text-sm font-bold leading-snug text-[#131517]">
              Use Oldest Empty First
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <CustomSwitch
              checked={swSameWarehouseReuse}
              onChange={(next) => setSwSameWarehouseReuse(next)}
            />
            <span className="text-sm font-bold leading-snug text-[#131517]">
              Same Warehouse Reuse
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <CustomSwitch
              checked={swSameCityReuse}
              onChange={(next) => setSwSameCityReuse(next)}
            />
            <span className="text-sm font-bold leading-snug text-[#131517]">
              Same City Reuse
            </span>
          </div>
        </div>

        <div className="mt-6 flex w-full min-w-0 max-w-full flex-row flex-wrap items-start gap-4">
          <div
            className={`${appointmentStyles.LicenseContainer} min-w-0 shrink-0`}
            style={{ width: FIELD_W, maxWidth: FIELD_W }}>
            <span className="font-averta mb-2 block text-sm font-bold text-primary-text">
              Max Distance (Min)
            </span>
            <input
              type="number"
              className={appointmentStyles.LicenseInput}
              value={swMaxDistance}
              onChange={(e) => setSwMaxDistance(e.target.value)}
              placeholder="Minutes"
              min={16}
              style={inputStyle}
            />
          </div>
          <div
            className={`${appointmentStyles.LicenseContainer} min-w-0 shrink-0`}
            style={{ width: FIELD_W, maxWidth: FIELD_W }}>
            <span className="font-averta mb-2 block text-sm font-bold text-primary-text">
              Buffer Time (Hrs)
            </span>
            <input
              type="number"
              className={appointmentStyles.LicenseInput}
              value={swBeforeTargerReturn}
              onChange={(e) => setSwBeforeTargerReturn(e.target.value)}
              placeholder="Hours"
              min={0}
              style={inputStyle}
            />
          </div>
          <div
            className={`${appointmentStyles.LicenseContainer} min-w-0 shrink-0`}
            style={{ width: FIELD_W, maxWidth: FIELD_W }}>
            <span className="font-averta mb-2 block text-sm font-bold text-primary-text">
              Before Target Return (Min)
            </span>
            <input
              type="number"
              className={appointmentStyles.LicenseInput}
              value={swAfterTargerReturn}
              onChange={(e) => setSwAfterTargerReturn(e.target.value)}
              placeholder="Hours"
              min={0}
              style={inputStyle}
            />
          </div>
          <div
            className={`${appointmentStyles.LicenseContainer} min-w-0 shrink-0`}
            style={{ width: FIELD_W, maxWidth: FIELD_W }}>
            <span className="font-averta mb-2 block text-sm font-bold text-primary-text">
              After Target Return (Min)
            </span>
            <input
              type="number"
              className={appointmentStyles.LicenseInput}
              value={swMinExcessThreshold}
              onChange={(e) => setSwMinExcessThreshold(e.target.value)}
              placeholder="Hours"
              min={0}
              style={inputStyle}
            />
          </div>
        </div>
      </section>

      <AutobookRuleWarehouseGroupsSection
        instanceId="drop-pick-sharing"
        sectionTitle="Balancing and sharing groups"
        intro={SHARING_GROUPS_INTRO}
        subsectionTitle="Approved groups for Drop &amp; Pick balancing"
        catalogWarehouseGroups={catalogWarehouseGroups}
        selectedGroupCatalogIds={selectedSwSharingGroupCatalogIds}
        setSelectedGroupCatalogIds={setSelectedSwSharingGroupCatalogIds}
        catalogCustomers={catalogCustomers}
        catalogWarehouses={catalogWarehouses}
        draftRuleUuid={draftRuleUuid}
      />

      <div className="w-full min-w-0 max-w-full border-0 border-t border-solid border-[#e8e8e8] pt-5">
        <p className="m-0 mt-2 max-w-[920px] shrink-0 text-left text-sm font-bold leading-relaxed text-[#717784]">
          {DROP_PICK_SUBTITLE_2}
        </p>
        <AutobookTimeDateSelector
          persistToContainer={false}
          settings={swRulePreferences}
          onPreferencesChange={onSwRulePreferencesChange}
          preferencesSyncKey={`${preferencesSyncKey}-sw`}
          hideAddRangePrioritySelect
          hidePreferencesHeader
          rulesWeekdayDesign
          rulesTerminalDesign
          lfdDate={null}
          autobookMode={autobookModeOff}
          setHistoryList={noopHistory}
        />
      </div>
    </div>
  );
};
